import React, { useState, useEffect } from "react";
import "./OrderDetailModal.css";
import "../AdminPrintStyles.css";
import { FaUser, FaShippingFast, FaCreditCard, FaBoxOpen, FaDownload } from "react-icons/fa";
import { adminGetSettings } from "../api/admin";
import html2pdf from 'html2pdf.js';

const OrderDetailsModal = ({ order, onClose }) => {
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const orderId = order._id || order.id;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await adminGetSettings();
        setSiteSettings(res.data);
      } catch (err) {
        console.error("Failed to load settings in modal", err);
      }
    };
    fetchSettings();
  }, []);

  const handleDownloadPDF = () => {
    const element = document.getElementById('receipt-pdf-content');
    const opt = {
      margin: 0,
      filename: `Receipt_${String(orderId).slice(-6).toUpperCase()}.pdf`,
      image: { type: 'jpeg', quality: 1.0 },
      html2canvas: { scale: 3, useCORS: true, logging: false, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    html2pdf().from(element).set(opt).save().then(() => setShowPDFPreview(false));
  };

  const addr = order.shippingAddress || {};
  const displayAddress = {
    name: addr.fullName || addr.name || order.user?.name || "N/A",
    street: addr.street || addr.addressLine1 || "N/A",
    city: addr.city || "N/A",
    state: addr.state || "N/A",
    pincode: addr.pincode || addr.postalCode || "N/A",
    country: addr.country || "India",
    mobile: addr.mobile || addr.phone || order.user?.phone || "N/A",
    landmark: addr.landmark || ""
  };

  return (
    <div className="order-details-premium">
      <div className="detail-modal-custom-header" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <img src="/Images/nbitez_logo.jpeg" alt="Logo" style={{ height: '50px', borderRadius: '8px' }} />
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '900', color: '#181c32' }}>nbitez</h1>
        </div>
        <h2 style={{ margin: 0, textAlign: 'center' }}>TAX INVOICE</h2>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button 
            className="btn-download-top" 
            onClick={() => setShowPDFPreview(true)}
          >
            <FaDownload /> Download Receipt
          </button>
        </div>
      </div>

      <div className="premium-form-body">
        
        {/* Main Content Start */}
        <div id="receipt-full-body">
            {/* Header Stats */}
            <div className="order-hero-stats">
              <div className="hero-stat-item">
                <span className="stat-label">Order ID</span>
                <span className="stat-value">#{String(orderId).slice(-8).toUpperCase()}</span>
              </div>
              <div className="hero-stat-item">
                <span className="stat-label">Order Date</span>
                <span className="stat-value">{new Date(order.createdAt).toLocaleDateString(undefined, {
                  day: 'numeric', month: 'long', year: 'numeric'
                })}</span>
              </div>
              <div className="hero-stat-item">
                <span className="stat-label">Current Status</span>
                <span className={`status-pill-large ${(order.status || 'ORDER PLACED').toLowerCase().replace(/\s+/g, '-')}`}>{order.status}</span>
              </div>
            </div>

            {/* 4-Column Info Grid */}
            <div className="info-cards-grid">
              <div className="info-card store-card">
                <div className="card-header">
                  <FaBoxOpen className="card-icon" />
                  <h4>From Address</h4>
                </div>
                <div className="card-content">
                  <p><b>Store:</b> {siteSettings?.site_name || "Nbitez Premium"}</p>
                  <p><b>Address:</b> {siteSettings?.address || "Loading store address..."}</p>
                  <p><b>Email:</b> {siteSettings?.contact_email}</p>
                </div>
              </div>

              <div className="info-card">
                <div className="card-header">
                  <FaUser className="card-icon" />
                  <h4>Customer Info</h4>
                </div>
                <div className="card-content">
                  <p><b>Name:</b> {order.user?.name || displayAddress.name}</p>
                  <p><b>Phone:</b> {order.user?.phone || displayAddress.mobile}</p>
                  <p><b>Email:</b> {order.user?.email || "N/A"}</p>
                </div>
              </div>

              <div className="info-card">
                <div className="card-header">
                  <FaShippingFast className="card-icon" />
                  <h4>Shipping Details</h4>
                </div>
                <div className="card-content">
                  <p><b>Recipient:</b> {displayAddress.name}</p>
                  <p>{displayAddress.street}, {displayAddress.city}</p>
                  <p>{displayAddress.state} - {displayAddress.pincode}</p>
                  <p>{displayAddress.country}</p>
                </div>
              </div>

              <div className="info-card">
                <div className="card-header">
                  <FaCreditCard className="card-icon" />
                  <h4>Payment Info</h4>
                </div>
                <div className="card-content">
                  <p><b>Method:</b> {order.paymentMethod || "COD"}</p>
                  <p><b>Provider:</b> {order.paymentResult?.provider || order.paymentProvider || "N/A"}</p>
                  <p className="payment-status-badge">
                    {order.paymentResult?.status || "Success"}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Items Table */}
            <div className="order-items-section">
              <table className="items-mini-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Attributes</th>
                    <th>Price</th>
                    <th>Qty</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.orderItems || []).map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="item-name-cell">
                          <span className="item-title">{item.name || item.product?.title || "Product"}</span>
                        </div>
                      </td>
                      <td><span className="attr-badge">{item.attribute || item.variantId?.attribute || "Standard"}</span></td>
                      <td>₹{(item.offer_price || item.price || 0).toLocaleString()}</td>
                      <td>x {item.quantity}</td>
                      <td style={{ textAlign: 'right', fontWeight: '700' }}>
                        ₹{((item.offer_price || item.price || 0) * item.quantity).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Financial Summary & Status Update */}
            <div className="bottom-sections-grid">
               <div className="summary-card" style={{ visibility: 'hidden', height: 0, padding: 0 }}>
                  {/* Spacer/Placeholders if needed for layout */}
               </div>               <div className="status-control-card">
                  <div className="summary-section-header">
                    <h4>Financial Summary</h4>
                  </div>
                  
                  <div className="summary-details">
                    <div className="summary-row">
                      <span>Items Subtotal</span>
                      <span>₹{(order.itemsPrice || order.itemsTotal || 0).toLocaleString()}</span>
                    </div>
                    <div className="summary-row">
                      <span>Delivery Fee</span>
                      <span>₹{(order.shippingFee || order.deliveryFee || 0).toLocaleString()}</span>
                    </div>
                    <div className="summary-row">
                      <span>Platform Fee</span>
                      <span>₹{(order.platformFee || 0).toLocaleString()}</span>
                    </div>
                    {order.discountAmount > 0 && (
                      <div className="summary-row discount">
                        <span>Wallet Discount</span>
                        <span>- ₹{order.discountAmount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="summary-row grand-total">
                      <span>Grand Total</span>
                      <span>₹{(order.totalPrice || order.totalAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>

                   {order.trackingNumber && (
                    <div className="tracking-info-box">
                      <p className="tracking-label">Tracking Number</p>
                      <p className="tracking-value">{order.trackingNumber}</p>
                    </div>
                  )}

                  {/* Return Info */}
                  <div className="summary-details" style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                    {order.deliveredAt && (
                      <div className="summary-row">
                        <span style={{ fontSize: '0.85rem' }}>Delivered On</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>{new Date(order.deliveredAt).toLocaleDateString()}</span>
                      </div>
                    )}
                    {order.cancelReason && (
                      <div className="summary-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                        <span style={{ color: '#f1416c', fontWeight: '700', fontSize: '0.85rem' }}>Cancel Reason:</span>
                        <span style={{ fontStyle: 'italic', color: '#3f4254' }}>"{order.cancelReason}"</span>
                      </div>
                    )}
                    {order.returnReason && (
                      <div className="summary-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                        <span style={{ color: '#0095e8', fontWeight: '700', fontSize: '0.85rem' }}>Return Reason:</span>
                        <span style={{ fontStyle: 'italic', color: '#3f4254' }}>"{order.returnReason}"</span>
                      </div>
                    )}
                    {order.refundStatus && order.refundStatus !== "NONE" && (
                      <div className="summary-row">
                        <span>Refund Status</span>
                        <span className={`status-pill sm ${order.refundStatus.toLowerCase()}`}>{order.refundStatus}</span>
                      </div>
                    )}
                    {order.pickupStatus && order.pickupStatus !== "NONE" && (
                      <div className="summary-row">
                        <span>Pickup Status</span>
                        <span className={`status-pill sm ${order.pickupStatus.toLowerCase()}`}>{order.pickupStatus}</span>
                      </div>
                    )}
                  </div>
               </div>
            </div>
        </div>
      </div>

      {/* PDF Preview Modal for Invoice */}
      {showPDFPreview && (
        <div className="modal-overlay pdf-preview-modal" style={{ zIndex: 3000 }}>
          <div className="modal-content" style={{ maxWidth: '850px', backgroundColor: '#f0f2f5', height: '90vh' }}>
            <div className="modal-header">
              <h3>Receipt Preview - A4 Format</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-save" onClick={handleDownloadPDF}>Download PDF</button>
                <button className="btn-cancel" onClick={() => setShowPDFPreview(false)}>Close</button>
              </div>
            </div>
            <div className="preview-modal-body pdf-scroll-wrapper" style={{ 
              padding: 0, 
              overflowY: 'auto', 
              backgroundColor: '#525659',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <div id="receipt-pdf-content" className="pdf-preview-content" style={{ 
                backgroundColor: 'white', 
                padding: '20mm', 
                width: '210mm',
                boxShadow: '0 0 10px rgba(0,0,0,0.5)',
                minHeight: '296mm',
                boxSizing: 'border-box',
                position: 'relative',
                fontFamily: "'Outfit', sans-serif",
                overflow: 'hidden'
              }}>
                {/* Header Section */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginBottom: '40px', borderBottom: '4px solid #181c32', paddingBottom: '20px' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <img src="/Images/nbitez_logo.jpeg" alt="Logo" style={{ height: '60px', borderRadius: '10px' }} />
                      <h1 style={{ margin: 0, color: '#181c32', fontSize: '28px', fontWeight: '900' }}>nbitez</h1>
                   </div>
                   <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#181c32', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', fontSize: '24px' }}>Tax Invoice</div>
                   </div>
                   <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: '#181c32' }}></div>
                      <div style={{ color: '#7e8299', fontSize: '13px' }}>Order ID: #{String(orderId).slice(-10).toUpperCase()}</div>
                   </div>
                </div>

                <div style={{ display: 'flex', gap: '40px', marginBottom: '40px' }}>
                   <div style={{ flex: 1 }}>
                      <div style={{ color: '#7e8299', fontSize: '11px', textTransform: 'uppercase', fontWeight: '800', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>From:</div>
                      <div style={{ fontSize: '15px', fontWeight: '800', color: '#181c32', marginBottom: '5px' }}>{siteSettings?.site_name || "Nbitez Premium"}</div>
                      <div style={{ fontSize: '13px', color: '#3f4254', lineHeight: '1.5' }}>{siteSettings?.address || "Building No. 42, Culinary Heights, Foodie District"}</div>
                      <div style={{ fontSize: '13px', color: '#3f4254', marginTop: '5px' }}>Email: {siteSettings?.contact_email}</div>
                      <div style={{ fontSize: '13px', color: '#3f4254' }}>Phone: {siteSettings?.contact_phone}</div>
                   </div>
                   <div style={{ flex: 1 }}>
                      <div style={{ color: '#7e8299', fontSize: '11px', textTransform: 'uppercase', fontWeight: '800', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Bill To:</div>
                      <div style={{ fontSize: '15px', fontWeight: '800', color: '#181c32', marginBottom: '5px' }}>{displayAddress.name}</div>
                      <div style={{ fontSize: '13px', color: '#3f4254', lineHeight: '1.5' }}>{displayAddress.street}</div>
                      <div style={{ fontSize: '13px', color: '#3f4254' }}>{displayAddress.city}, {displayAddress.state} - {displayAddress.pincode}</div>
                      <div style={{ fontSize: '13px', color: '#3f4254', marginTop: '5px' }}>{displayAddress.mobile}</div>
                   </div>
                   <div style={{ width: '150px' }}>
                      <div style={{ color: '#7e8299', fontSize: '11px', textTransform: 'uppercase', fontWeight: '800', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Details:</div>
                      <div style={{ fontSize: '13px', marginBottom: '5px' }}>Date: <b>{new Date(order.createdAt).toLocaleDateString('en-GB')}</b></div>
                      <div style={{ fontSize: '13px', marginBottom: '5px' }}>Method: <b>{order.paymentMethod || "COD"}</b></div>
                      <div style={{ fontSize: '13px' }}>Status: <b>{order.status}</b></div>
                   </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '40px' }}>
                   <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #181c32' }}>
                         <th style={{ padding: '15px', textAlign: 'left', fontSize: '12px', color: '#181c32' }}>DESCRIPTION</th>
                         <th style={{ padding: '15px', textAlign: 'center', fontSize: '12px', color: '#181c32', width: '80px' }}>QTY</th>
                         <th style={{ padding: '15px', textAlign: 'right', fontSize: '12px', color: '#181c32', width: '120px' }}>UNIT PRICE</th>
                         <th style={{ padding: '15px', textAlign: 'right', fontSize: '12px', color: '#181c32', width: '120px' }}>AMOUNT</th>
                      </tr>
                   </thead>
                   <tbody>
                      {(order.orderItems || []).map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                           <td style={{ padding: '15px' }}>
                              <div style={{ fontWeight: '700', color: '#181c32' }}>{item.name || item.product?.title}</div>
                              <div style={{ fontSize: '11px', color: '#7e8299', marginTop: '3px' }}>{item.attribute || "Standard Version"}</div>
                           </td>
                           <td style={{ padding: '15px', textAlign: 'center', color: '#3f4254' }}>{item.quantity}</td>
                           <td style={{ padding: '15px', textAlign: 'right', color: '#3f4254' }}>₹{(item.offer_price || item.price || 0).toLocaleString()}</td>
                           <td style={{ padding: '15px', textAlign: 'right', fontWeight: '700', color: '#181c32' }}>₹{((item.offer_price || item.price || 0) * item.quantity).toLocaleString()}</td>
                        </tr>
                      ))}
                   </tbody>
                </table>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '20px' }}>
                   <div style={{ flex: 1 }}>
                      {order.trackingNumber && (
                        <div style={{ backgroundColor: '#f1faff', padding: '15px', borderRadius: '8px', border: '1px dashed #3699ff', display: 'inline-block' }}>
                           <div style={{ color: '#0095e8', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px' }}>Tracking Number</div>
                           <div style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: '800', color: '#181c32' }}>{order.trackingNumber}</div>
                        </div>
                      )}
                   </div>
                    <div style={{ width: '300px', backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <span style={{ color: '#7e8299', fontSize: '13px' }}>Items Subtotal:</span>
                          <span style={{ fontWeight: '600', color: '#181c32' }}>₹{(order.itemsPrice || 0).toLocaleString()}</span>
                       </div>
                       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <span style={{ color: '#7e8299', fontSize: '13px' }}>Delivery Fee:</span>
                          <span style={{ fontWeight: '600', color: '#181c32' }}>₹{(order.shippingFee || 0).toLocaleString()}</span>
                       </div>
                       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <span style={{ color: '#7e8299', fontSize: '13px' }}>Platform Fee:</span>
                          <span style={{ fontWeight: '600', color: '#181c32' }}>₹{(order.platformFee || 0).toLocaleString()}</span>
                       </div>
                       {order.discountAmount > 0 && (
                         <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#f1416c' }}>
                            <span style={{ fontSize: '13px' }}>Wallet Discount:</span>
                            <span>- ₹{order.discountAmount.toLocaleString()}</span>
                         </div>
                       )}
                       <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #181c32', marginTop: '10px', paddingTop: '15px' }}>
                          <span style={{ fontSize: '16px', fontWeight: '900', color: '#181c32' }}>GRAND TOTAL:</span>
                          <span style={{ fontSize: '18px', fontWeight: '900', color: '#181c32' }}>₹{(order.totalPrice || 0).toLocaleString()}</span>
                       </div>
                    </div>
                </div>

                <div style={{ position: 'absolute', bottom: '20mm', left: '20mm', right: '20mm' }}>
                    <div style={{ borderTop: '1px solid #eff2f5', paddingTop: '20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#181c32', marginBottom: '8px' }}>Thank you for shopping with nbitez</div>
                        <div style={{ fontSize: '12px', color: '#7e8299' }}>For any queries, contact : {siteSettings?.contact_email || "alverleathers@gmail.com"}</div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsModal;
